import React, { useEffect, useState } from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import { getContactNames } from "../../Api/TicketApiOrg";
import "./ProjectTasksTab.css";

const ProjectTasksTab = ({
  // Data
  stages,
  filteredStageTasks,
  tasksLoading,
  tasksLoadingByStage,
  taskAssignmentsByTask,
  teamMemberDetails,
  resourcesByOrg,
  clientContactPersons,
  partnerContactPersons,
  contactNamesCache,
  setContactNamesCache,
  ticketNameMap,
  eventNameMap,
  taskBuildingBlocks,
  taskTypesLoading,
  taskStatusesLoading,
  handleBatchCreateClick,

  // Permissions
  hasEditPermission,

  // Handlers
  handleOpenTaskDetails,
  handleEditTask,
  handleSoftDeleteTask,
  openTaskDialog,
  getCurrentAssignedUsersForTask,
  getTaskTypeName,
  getTaskStatusName,
  getPriorityBadge,
  getStatusBadge,
  handleEditStage,
  handleCreateStage,
  setShowTaskOptions,
  showTaskOptions,
  handleExcelUploadClick,
  handleManualTaskClick,
  exportTasksToExcel,
  searchQuery,
  setSearchQuery,
  projectId,
}) => {
  // State for hover tooltip
  const [hoveredAssignee, setHoveredAssignee] = useState(null);

  // Function to fetch contact names for GUIDs (same as TaskDetailsPage)
  const fetchContactNames = async (guids) => {
    if (!guids || guids.length === 0) return;

    const validGuids = guids.filter(
      (g) =>
        g &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          g,
        ),
    );

    if (validGuids.length === 0) return;

    try {
      const res = await getContactNames(validGuids);
      const map = {};
      res.forEach((c) => {
        map[c.contactGuid.toLowerCase()] = c.contactName;
      });
      setContactNamesCache((prev) => ({
        ...prev,
        ...map,
      }));
    } catch (err) {
      console.error("Failed to fetch contact names", err);
    }
  };

  // Collect all GUIDs from assignments and fetch their names
  useEffect(() => {
    const allGuids = new Set();

    // Get all tasks from filteredStageTasks
    const allTasks = Object.values(filteredStageTasks).flat();

    allTasks.forEach((task) => {
      const assignments = taskAssignmentsByTask[task.taskId] || [];
      assignments.forEach((assignment) => {
        const userId = assignment.assignedUserId;
        // Check if it's a GUID format
        if (
          userId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            userId,
          )
        ) {
          if (!contactNamesCache[userId.toLowerCase()]) {
            allGuids.add(userId);
          }
        }
      });
    });

    if (allGuids.size > 0) {
      fetchContactNames(Array.from(allGuids));
    }
  }, [filteredStageTasks, taskAssignmentsByTask]);

  // Helper function to get ALL assignments for a task (not just active)
  const getAllAssignmentsForTask = (taskId) => {
    return taskAssignmentsByTask[taskId] || [];
  };

  // Helper function to get assignee name (resolves GUIDs, team members, contacts)
  const getAssigneeName = (userId) => {
    if (!userId) return "Unknown";

    // 1️⃣ Check contact names cache first (for GUIDs from getContactNames API)
    const contactName = contactNamesCache?.[userId?.toLowerCase()];
    if (contactName) return contactName;

    // 2️⃣ Check team members
    const teamMember = teamMemberDetails?.find(
      (user) => String(user.userId) === String(userId),
    );
    if (teamMember) return teamMember.username;

    // 3️⃣ Check client contacts
    const clientContact = clientContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (clientContact)
      return clientContact.contactPerName || clientContact.contactName;

    // 4️⃣ Check partner contacts
    const partnerContact = partnerContactPersons?.find(
      (c) => c.cusVenId === userId || c.contactGuid === userId,
    );
    if (partnerContact)
      return partnerContact.contactPerName || partnerContact.contactName;

    // 5️⃣ Check resources by org
    const resource = resourcesByOrg?.find(
      (res) => String(res.resourceId) === String(userId),
    );
    if (resource) return resource.name;

    // 6️⃣ Check if it's a GUID (UUID format) - fallback to truncated
    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        userId,
      );
    if (isGuid) return userId.substring(0, 8);

    return userId;
  };

  // Helper function to get initials from name (max 2-4 characters)
  // Helper function to get initials from name - ALWAYS returns exactly 2 letters
  const getInitials = (name) => {
    if (!name || name === "Unknown") return "??";

    // Remove any extra spaces and trim
    const cleanName = name.trim();

    // If name is already 2 letters, return as uppercase
    if (cleanName.length === 2) return cleanName.toUpperCase();

    // If name has spaces (first name + last name)
    const words = cleanName.split(/\s+/);
    if (words.length >= 2) {
      // Take first letter of first word and first letter of last word
      const firstInitial = words[0][0]?.toUpperCase() || "";
      const lastInitial = words[words.length - 1][0]?.toUpperCase() || "";
      return firstInitial + lastInitial;
    }

    // For single name, take first 2 letters
    if (cleanName.length >= 2) {
      return cleanName.substring(0, 2).toUpperCase();
    }

    // If only 1 character, return it with a placeholder
    if (cleanName.length === 1) {
      return cleanName[0].toUpperCase() + "?";
    }

    return "??";
  };

  // Helper function to get avatar color based on userId
  const getAvatarColor = (userId) => {
    const colors = [
      "#6366f1", // Indigo
      "#8b5cf6", // Purple
      "#ec4899", // Pink
      "#f43f5e", // Rose
      "#ef4444", // Red
      "#f97316", // Orange
      "#f59e0b", // Amber
      "#eab308", // Yellow
      "#84cc16", // Lime
      "#10b981", // Emerald
      "#14b8a6", // Teal
      "#06b6d4", // Cyan
      "#3b82f6", // Blue
      "#6b7280", // Gray
    ];
    let hash = 0;
    const str = String(userId);
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Helper to get priority display
  const getPriorityDisplay = (priority) => {
    if (priority === "High") {
      return (
        <div className="ptt-priority-high">
          <i className="fas fa-flag"></i>
          <span>High</span>
        </div>
      );
    }
    if (priority === "Medium") {
      return (
        <div className="ptt-priority-medium">
          <i className="fas fa-flag"></i>
          <span>Medium</span>
        </div>
      );
    }
    return (
      <div className="ptt-priority-low">
        <i className="fas fa-flag"></i>
        <span>Low</span>
      </div>
    );
  };

  // Helper to get status display
  const getStatusDisplay = (status) => {
    const statusName = getTaskStatusName(status);
    if (statusName === "Completed") {
      return <span className="ptt-status-completed">{statusName}</span>;
    }
    if (statusName === "In Progress" || statusName === "Active") {
      return <span className="ptt-status-active">{statusName}</span>;
    }
    return <span className="ptt-status-pending">{statusName}</span>;
  };

  // Render assignee with tooltip on hover
  const renderAssigneeWithTooltip = (assigneeName, userId, assignmentId) => {
    const initials = getInitials(assigneeName);
    const avatarColor = getAvatarColor(userId);

    return (
      <OverlayTrigger
        key={assignmentId}
        placement="top"
        overlay={
          <Tooltip
            id={`tooltip-${assignmentId}`}
            className="ptt-assignee-tooltip"
          >
            <strong>{assigneeName}</strong>
            <br />
            <small className="text-muted">Click to manage</small>
          </Tooltip>
        }
      >
        <div
          className="ptt-assignee-avatar"
          style={{ backgroundColor: avatarColor }}
          onClick={() => openTaskDialog({ taskId: userId })}
        >
          {initials}
        </div>
      </OverlayTrigger>
    );
  };

  return (
    <div className="ptt-tasks-container">
      <div className="ptt-task-list-container">
        {/* Controls Bar */}
        <div className="ptt-controls-bar">
          {hasEditPermission && (
            <>
              <div className="ptt-dropdown">
                <button
                  className="ptt-btn-primary"
                  onClick={() => setShowTaskOptions(!showTaskOptions)}
                >
                  Add Task
                </button>
                {showTaskOptions && (
  <>
    <div
      className="ptt-overlay"
      onClick={() => setShowTaskOptions(false)}
    />
    <div className="ptt-dropdown-menu">
      {/* Option 1: Bulk Create Tasks */}
      <div
        className="ptt-dropdown-item"
        onClick={handleBatchCreateClick}
      >
        <i className="fas fa-layer-group me-2"></i>
        <div>
          <span>Bulk Create Tasks</span>
          <small className="text-muted d-block" style={{ fontSize: "10px" }}>
            Create multiple tasks at once
          </small>
        </div>
      </div>
      
      {/* Option 2: Import from Excel */}
      <div
        className="ptt-dropdown-item"
        onClick={handleExcelUploadClick}
      >
        <i className="fas fa-file-excel me-2 text-success"></i>
        <div>
          <span>Import from Excel</span>
          <small className="text-muted d-block" style={{ fontSize: "10px" }}>
            Upload Excel file with tasks
          </small>
        </div>
      </div>
      
      {/* Option 3: Create Single Task */}
      <div
        className="ptt-dropdown-item"
        onClick={handleManualTaskClick}
      >
        <i className="fas fa-plus-circle me-2 text-primary"></i>
        <div>
          <span>Create Single Task</span>
          <small className="text-muted d-block" style={{ fontSize: "10px" }}>
            Add one task manually
          </small>
        </div>
      </div>
    </div>
  </>
)}
              </div>

              <button className="ptt-btn-primary" onClick={handleCreateStage}>
                Add Stage
              </button>
            </>
          )}

          <div className="ptt-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search tasks by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: "0 4px",
                }}
              >
                <i
                  className="fas fa-times"
                  style={{ fontSize: "10px", color: "#94a3b8" }}
                ></i>
              </button>
            )}
          </div>

          <button
            className="ptt-btn-primary"
            onClick={exportTasksToExcel}
            title="Export to Excel"
          >
            <i className="fas fa-download"></i> Export
          </button>
        </div>

        {/* Loading State */}
        {tasksLoading && (
          <div className="ptt-loading">
            <div className="ptt-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        )}

        {/* Tasks Content */}
        {!tasksLoading && (
          <div>
            {/* Render Stages */}
            {stages.map((stage) => {
              const stageTasks = filteredStageTasks[stage.stageId] || [];

              return (
                <div key={stage.stageId} className="ptt-stage-section">
                  <div className="ptt-stage-header">
                    <div className="ptt-stage-title-wrapper">
                      <div className="ptt-stage-indicator"></div>
                      <span className="ptt-stage-title">{stage.stageName}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="ptt-stage-badge">
                        {stageTasks.length} tasks
                      </span>
                      {hasEditPermission && (
                        <button
                          className="ptt-edit-stage-btn"
                          onClick={() => handleEditStage(stage)}
                          title="Edit Stage"
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="ptt-task-table">
                      <thead>
                        <tr>
                          <th style={{ width: "7%" }}>TaskId</th>
                          <th className="ptt-col-name">Name</th>
                          <th className="ptt-col-assignee">Assignee</th>
                          <th className="ptt-col-enddate">End Date</th>
                          <th className="ptt-col-priority">Priority</th>
                          <th className="ptt-col-status">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasksLoadingByStage[stage.stageId] ? (
                          <tr>
                            <td colSpan="6" className="text-center py-3">
                              <div
                                className="ptt-spinner"
                                style={{ width: "24px", height: "24px" }}
                              ></div>
                            </td>
                          </tr>
                        ) : stageTasks.length === 0 ? (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center py-3 text-muted"
                            >
                              No tasks found in this stage
                            </td>
                          </tr>
                        ) : (
                          stageTasks.map((task) => {
                            // Get ALL assignments for this task
                            const allAssignments = getAllAssignmentsForTask(
                              task.taskId,
                            );

                            return (
                              <tr key={task.taskId} className="ptt-task-row">
                                <td className="ptt-task-id-cell">
                                  {task.taskId}
                                </td>

                                {/* Task Name */}
                                <td className="ptt-col-name">
                                  <div className="ptt-task-name">
                                    <i className="fas fa-play ptt-play-icon"></i>
                                    <i className="fas fa-check-circle ptt-check-icon"></i>
                                    <a
                                      href={`/project/${task.projectBelong || projectId}/task/${task.taskId}`}
                                      className="ptt-task-name-text"
                                      onClick={(e) => {
                                        if (
                                          !e.ctrlKey &&
                                          !e.metaKey &&
                                          e.button !== 1
                                        ) {
                                          e.preventDefault();
                                          handleOpenTaskDetails(task.taskId);
                                        }
                                      }}
                                      title="Click to view task details (Ctrl+Click to open in new tab)"
                                    >
                                      {task.taskName}
                                    </a>
                                  </div>
                                </td>

                                {/* Assignees - Show as initials only, full name on hover */}
                                <td className="ptt-col-assignee">
                                  {allAssignments.length === 0 ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="ptt-unassigned-text">
                                        Unassigned
                                      </span>
                                      {hasEditPermission && (
                                        <button
                                          className="ptt-assign-btn"
                                          // onClick={() => openTaskDialog(task)}
                                          title="Manage Assignments"
                                        >
                                          <i className="fas fa-user-plus"></i>
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="ptt-assignee-avatars-container">
                                      {allAssignments.map((assignment, idx) => {
                                        const assigneeName = getAssigneeName(
                                          assignment.assignedUserId,
                                        );
                                        const initials =
                                          getInitials(assigneeName);
                                        const avatarColor = getAvatarColor(
                                          assignment.assignedUserId,
                                        );

                                        return (
                                          <OverlayTrigger
                                            key={assignment.assignmentId || idx}
                                            placement="top"
                                            overlay={
                                              <Tooltip
                                                id={`tooltip-${assignment.assignmentId || idx}`}
                                                className="ptt-assignee-tooltip"
                                              >
                                                <div className="text-center">
                                                  <strong>
                                                    {assigneeName}
                                                  </strong>
                                                  <br />
                                                  {/* <small className="text-muted">
                                                    Click to manage
                                                  </small> */}
                                                </div>
                                              </Tooltip>
                                            }
                                          >
                                            <div
                                              className="ptt-assignee-avatar"
                                              style={{
                                                backgroundColor: avatarColor,
                                              }}
                                              // onClick={(e) => {
                                              //   e.stopPropagation();
                                              //   openTaskDialog(task);
                                              // }}
                                              title={assigneeName}
                                            >
                                              {initials}
                                            </div>
                                          </OverlayTrigger>
                                        );
                                      })}
                                      {hasEditPermission && (
                                        <button
                                          className="ptt-assign-btn-icon"
                                          onClick={() => openTaskDialog(task)}
                                          title="Manage Assignments"
                                        >
                                          <i className="fas fa-edit"></i>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* End Date */}
                                <td className="ptt-date-cell">
                                  {task.taskEndDate
                                    ? moment(task.taskEndDate).format(
                                        "MMM DD, YYYY",
                                      )
                                    : "-"}
                                </td>

                                {/* Priority */}
                                <td className="text-center">
                                  {getPriorityDisplay(
                                    task.taskPriority || "No Priority",
                                  )}
                                </td>

                                {/* Status */}
                                <td className="text-center">
                                  {getStatusDisplay(task.taskStatus)}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {/* No Stage Section */}
            {(filteredStageTasks["no-stage"] || []).length > 0 && (
              <div className="ptt-stage-section ptt-no-stage-section">
                <div className="ptt-stage-header">
                  <div className="ptt-stage-title-wrapper">
                    <div className="ptt-stage-indicator"></div>
                    <span className="ptt-stage-title">No Stage Assigned</span>
                  </div>
                  <div>
                    <span className="ptt-stage-badge">
                      {filteredStageTasks["no-stage"].length} tasks
                    </span>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="ptt-task-table">
                    <thead>
                      <tr>
                        <th style={{ width: "7%" }}>TaskId</th>
                        <th className="ptt-col-name">Name</th>
                        <th className="ptt-col-assignee">Assignee</th>
                        <th className="ptt-col-enddate">End Date</th>
                        <th className="ptt-col-priority">Priority</th>
                        <th className="ptt-col-status">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStageTasks["no-stage"].map((task) => {
                        const allAssignments = getAllAssignmentsForTask(
                          task.taskId,
                        );

                        return (
                          <tr key={task.taskId} className="ptt-task-row">
                            <td className="ptt-task-id-cell">{task.taskId}</td>

                            <td className="ptt-col-name">
                              <div className="ptt-task-name">
                                <i className="fas fa-play ptt-play-icon"></i>
                                <i className="fas fa-check-circle ptt-check-icon"></i>
                                <a
                                  href={`/project/${task.projectBelong || projectId}/task/${task.taskId}`}
                                  className="ptt-task-name-text"
                                  onClick={(e) => {
                                    if (
                                      !e.ctrlKey &&
                                      !e.metaKey &&
                                      e.button !== 1
                                    ) {
                                      e.preventDefault();
                                      handleOpenTaskDetails(task.taskId);
                                    }
                                  }}
                                  title="Click to view task details (Ctrl+Click to open in new tab)"
                                >
                                  {task.taskName}
                                </a>
                              </div>
                            </td>

                            <td className="ptt-col-assignee">
                              {allAssignments.length === 0 ? (
                                <div className="d-flex align-items-center gap-2">
                                  <span className="ptt-unassigned-text">
                                    Unassigned
                                  </span>
                                  {hasEditPermission && (
                                    <button
                                      className="ptt-assign-btn"
                                      onClick={() => openTaskDialog(task)}
                                      title="Manage Assignments"
                                    >
                                      <i className="fas fa-user-plus"></i>
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="ptt-assignee-avatars-container">
                                  {allAssignments.map((assignment, idx) => {
                                    const assigneeName = getAssigneeName(
                                      assignment.assignedUserId,
                                    );
                                    const initials = getInitials(assigneeName);
                                    const avatarColor = getAvatarColor(
                                      assignment.assignedUserId,
                                    );

                                    return (
                                      <OverlayTrigger
                                        key={assignment.assignmentId || idx}
                                        placement="top"
                                        overlay={
                                          <Tooltip
                                            id={`tooltip-${assignment.assignmentId || idx}`}
                                            className="ptt-assignee-tooltip"
                                          >
                                            <div className="text-center">
                                              <strong>{assigneeName}</strong>
                                              <br />
                                              <small className="text-muted">
                                                Click to manage
                                              </small>
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <div
                                          className="ptt-assignee-avatar"
                                          style={{
                                            backgroundColor: avatarColor,
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTaskDialog(task);
                                          }}
                                          title={assigneeName}
                                        >
                                          {initials}
                                        </div>
                                      </OverlayTrigger>
                                    );
                                  })}
                                  {hasEditPermission && (
                                    <button
                                      className="ptt-assign-btn-icon"
                                      onClick={() => openTaskDialog(task)}
                                      title="Manage Assignments"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>

                            <td className="ptt-date-cell">
                              {task.taskEndDate
                                ? moment(task.taskEndDate).format(
                                    "MMM DD, YYYY",
                                  )
                                : "-"}
                            </td>
                            <td className="text-center">
                              {getPriorityDisplay(
                                task.taskPriority || "No Priority",
                              )}
                            </td>
                            <td className="text-center">
                              {getStatusDisplay(task.taskStatus)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State */}
            {stages.length === 0 && !filteredStageTasks["no-stage"]?.length && (
              <div className="ptt-empty-state">
                <i className="fas fa-columns"></i>
                <p>No stages found for this project.</p>
                {hasEditPermission && (
                  <button
                    className="ptt-btn-primary mt-2"
                    onClick={handleCreateStage}
                  >
                    Create First Stage
                  </button>
                )}
              </div>
            )}

            {stages.length > 0 &&
              !filteredStageTasks["no-stage"]?.length &&
              stages.every(
                (stage) => !filteredStageTasks[stage.stageId]?.length,
              ) && (
                <div className="ptt-empty-state">
                  <i className="fas fa-tasks"></i>
                  <p>No tasks found in any stage.</p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTasksTab;
